import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';
import {useLocation, matchPath} from 'react-router-dom';
// @mui
import {List, Stack, Collapse, InputAdornment, TextField} from '@mui/material';
//
import {StyledSubheader} from './styles';
import NavList from './NavList';
import {getIniLoggedUser, getSessionOperator} from "../../../helper/session";
import {AUTH} from "../../../auth/FirebaseContext";
import {useSelector} from "../../../redux/store";
import {USER_TYPES_GROUP} from "../../../constants";
import Iconify from "../../Iconify";

// ----------------------------------------------------------------------

NavSectionVertical.propTypes = {
    sx: PropTypes.object,
    data: PropTypes.array,
};

export default function NavSectionVertical({data, sx, ...other}) {
    const {pathname} = useLocation();
    const {forbiddenPages, userType} = useSelector(state => state.userSettings);
    const {channels: salesChannels} = useSelector(state => state.salesChannels);
    const OPERATOR = getSessionOperator().group;
    const primaryUser = getIniLoggedUser();
    const isPrimary = primaryUser?.uid === AUTH.currentUser.uid;

    const [searchQuery, setSearchQuery] = useState('');

    // Filter forbidden pages
    if (forbiddenPages) {
        data = data.map(value => {
            const items = value.items?.filter(item => !forbiddenPages.includes(item.path)) || [];
            return {...value, items};
        });
    }

    // Filter entire group based on userType and needPrimaryUser
    data = data.filter(value => shouldDisplayItem(value, userType, isPrimary));

    // Filter by role
    data = filterNavByRole(data, OPERATOR);

    const isSearching = searchQuery.trim().length > 0;

    // Vidljive sekcije se sklapaju pre prikaza jer od njih zavisi koja sekcija treba
    // da bude razvijena za trenutnu adresu.
    const sections = data
        .map((group) => {
            const key = group.subheader || group.items[0].title;
            let allItems = group?.items || [];
            // Filter top-level items
            allItems = allItems
                .filter(item => !item.needSalesChannel || salesChannels.length > 0)
                .filter(item => !item.primaryOnly || isPrimary)
                .filter(item => shouldDisplayItem(item, userType, isPrimary, true));

            // Filter children by role and userType
            allItems = allItems
                .filter(item => shouldDisplayItem(item, userType, isPrimary, false))
                .map(item => {
                    const children = item.children || [];
                    const filteredChildren = children.filter(child =>
                        (!item.roles || item.roles.includes(OPERATOR)) &&
                        shouldDisplayItem(item, userType, isPrimary, false, child)
                    );

                    return {
                        ...item,
                        children: filteredChildren,
                    };
                })
                .filter(item => {
                    // Ako ima children, mora bar jedan da ostane
                    if (item.children && item.children.length > 0) return true;

                    // Ako nema children, proveri da li je leaf (direktan link)
                    return !item.children || item.children.length === 0;
                });

            return {key, sectionKey: group.subheader || key, subheader: group.subheader, items: allItems};
        })
        .filter(section => section.items.length > 0);

    const activeSectionKey = findActiveSectionKey(sections, pathname);

    const [expandedSection, setExpandedSection] = useState(activeSectionKey || 'Početna');

    // Sekcija sa otvorenom stranicom ostaje razvijena i posle prelaska na drugu adresu
    // ili ponovnog učitavanja, da operater vidi gde se nalazi. Ručno sklapanje i dalje
    // radi - efekat se pokreće tek kad se promeni adresa ili spisak vidljivih sekcija.
    useEffect(() => {
        if (activeSectionKey) {
            setExpandedSection(activeSectionKey);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, activeSectionKey]);

    const handleToggleSection = (subheader) => {
        setExpandedSection(prev => prev === subheader ? null : subheader);
    };

    return (
        <Stack sx={sx} {...other}>
            <TextField
                size="small"
                placeholder="Pretraži meni..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Iconify icon="eva:search-fill" sx={{color: 'text.disabled', width: 20, height: 20}}/>
                        </InputAdornment>
                    ),
                }}
                sx={{mx: 2, mb: 1}}
            />

            {sections.map(({key, sectionKey, subheader, items}) => {
                let allItems = items;

                // Search filter
                if (isSearching) {
                    const query = searchQuery.trim().toLowerCase();
                    allItems = allItems.filter(item => {
                        if (item.title?.toLowerCase().includes(query)) return true;
                        if (item.children?.some(child => child.title?.toLowerCase().includes(query))) return true;
                        return false;
                    });
                }

                if (allItems.length === 0) return null;

                const isExpanded = isSearching || expandedSection === sectionKey;

                return (
                    <List
                        key={key}
                        disablePadding
                        sx={{
                            px: 2,
                            mb: 1,
                            mx: 2,
                            borderRadius: 1.5,
                            backgroundColor: "rgba(0, 171, 85, 0.12)",
                        }}
                    >
                        {subheader && (
                            <StyledSubheader
                                disableSticky
                                onClick={() => handleToggleSection(sectionKey)}
                                sx={{
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    userSelect: 'none',
                                    '&:hover': {opacity: 0.72},
                                }}
                            >
                                {subheader}
                                <Iconify
                                    icon={isExpanded ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'}
                                    width={16}
                                    sx={{ml: 1, flexShrink: 0}}
                                />
                            </StyledSubheader>
                        )}

                        <Collapse in={isExpanded} unmountOnExit>
                            {allItems.map((list) => (
                                <NavList
                                    key={list.title + list.path}
                                    data={list}
                                    depth={1}
                                    hasChild={Array.isArray(list.children) && list.children.length > 0}
                                    isPrimary={isPrimary}
                                    operator={OPERATOR}
                                />
                            ))}
                        </Collapse>
                    </List>
                );
            })}
        </Stack>
    );
}

// ----------------------------------------------------------------------

/**
 * Dužina putanje iz menija koja odgovara trenutnoj adresi, ili 0 ako ne odgovara.
 * Koren (`''`) i `/` se preskaču jer bi odgovarali svakoj adresi.
 */
const matchedPathLength = (path, pathname) =>
    typeof path === "string" && path.length > 1 && matchPath({path, end: false}, pathname)
        ? path.length
        : 0;

const deepestMatch = (items, pathname) =>
    (items || []).reduce((best, item) => Math.max(
        best,
        matchedPathLength(item.path, pathname),
        deepestMatch(item.children, pathname)
    ), 0);

/**
 * Sekcija u kojoj stoji otvorena stranica. Uzima se najduže poklapanje jer ista adresa
 * ume da pripada dvema sekcijama - /reports/internationalReport je i dete „Obračuna
 * prihoda prevoznika" i pod korenom /reports iz „Početne"; tačnija je ona prva.
 */
const findActiveSectionKey = (sections, pathname) => {
    let activeKey = null;
    let activeLength = 0;

    sections.forEach((section) => {
        const length = deepestMatch(section.items, pathname);
        if (length > activeLength) {
            activeLength = length;
            activeKey = section.sectionKey;
        }
    });

    return activeKey;
};

const shouldDisplayItem = (item, userType, isPrimary, topHeader, child) => {
    const isAllowedUserType = !item.allowedUserTypes || item.allowedUserTypes.includes(userType);
    const needsPrimary = item.needPrimaryUser ?? false;
    if (userType === USER_TYPES_GROUP.BUS_STATION) {
        if (topHeader === true && isPrimary === false && needsPrimary === true) {
            return false
        }
        if (isPrimary && !needsPrimary && item.subheader !== "Administracija stanice i sopstvenog prevoza") {
            return false;
        }

        if (child?.title === "Likvidatura") {
            return false;
        }

        if (!needsPrimary) {
            return true;
        }

        if (!isPrimary) {
            return true;
        }

        if (isAllowedUserType) {
            return true
        }

    } else if (userType === USER_TYPES_GROUP.CITY_TRANSPORT) {
        if (topHeader) {
            return true
        }
        return isAllowedUserType;
    } else if (userType === USER_TYPES_GROUP.CARRIER) {
        if (topHeader) {
            return true
        }
        if (isAllowedUserType) {
            return true;
        }
    }
    return false;
};


const filterNavByRole = (navItems, operator) => {
    return navItems.map(item => {
        if (item.roles && !item.roles.includes(operator)) {
            return {...item, items: []};
        }

        const subItems = filterItemsByRole(item.items || [], operator);
        const filteredSubItems = subItems.map(subItem => {
            if (!subItem.children) {
                return haveRole(subItem.roles, operator) ? subItem : null;
            }

            const children = filterItemsByRole(subItem.children || [], operator);
            return {...subItem, children};
        }).filter(Boolean);

        return {...item, items: filteredSubItems};
    });
};

const filterItemsByRole = (items, operatorGroup) => {
    return items.filter(item => haveRole(item.roles, operatorGroup));
};

const haveRole = (roles, operatorGroup) => {
    return roles === undefined || roles === null || roles.includes(operatorGroup);
};
